import type { ComponentPropsWithoutRef } from "react";

import { ActionButton, cn } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { PlusIcon } from "@eisox/icons";
import { useSelector } from "@tanstack/react-form";

import type { PeriodType } from "../helpers/schemas.helper";
import { periodSchema } from "../helpers/schemas.helper";
import { Period } from "./period";

export interface AddPeriodProps extends ComponentPropsWithoutRef<"div"> {
  onAdd?: (period: PeriodType) => void;
}

export const AddPeriod: React.FC<AddPeriodProps> = ({ onAdd, className, ...props }) => {
  const form = useAppForm({
    defaultValues: { begin: "", end: "" },
    validators: {
      onSubmit: periodSchema,
    },
    onSubmit: ({ value, formApi }) => {
      onAdd?.(value);
      formApi.reset();
    },
  });

  const begin = useSelector(form.store, state => state.values.begin);
  const end = useSelector(form.store, state => state.values.end);
  const isDirty = useSelector(form.store, state => state.isDirty);

  const handleAddPeriod = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    void form.handleSubmit();
  };

  return (
    <div {...props} className={cn("w-full", className)} data-testid="add-period">
      <form.AppForm>
        <div className="grid w-full grid-cols-[1fr_19px] items-center gap-[10px]">
          <Period
            begin={begin}
            end={end}
            onBeginChange={value => form.setFieldValue("begin", value ?? "")}
            onEndChange={value => form.setFieldValue("end", value ?? "")}
          />
          <ActionButton
            type="button"
            className="p-0 size-[19px] shrink-0 justify-center rounded-full"
            disabled={!isDirty}
            onClick={handleAddPeriod}
          >
            <PlusIcon className="size-[10px]" />
          </ActionButton>
        </div>
      </form.AppForm>
    </div>
  );
};

AddPeriod.displayName = "AddPeriod";
